import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatLocalDateTime } from '../../utils/dateUtils';
import { eventService, EventType } from '../../services/eventService';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BugReportIcon from '@mui/icons-material/BugReport';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import SuspensionFormDialog from '../suspensions/SuspensionFormDialog';
import SuspensionDeleteDialog from '../suspensions/SuspensionDeleteDialog';
import apiClient from '../../services/apiService';
import { suspensionApi, SuspensionRead } from '../../services/apiService';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import QrScannerComponent from '../../components/QrScannerComponent';
import SensorPhotoCapture from '../../components/SensorPhotoCapture';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../../contexts/AuthContext';

// Fix for missing marker icons in Leaflet
const iconUrl = 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png';
const shadowUrl = 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Component to handle map operations and marker drag events
interface MapControlsProps {
  position: [number, number];
  onPositionChange: (position: [number, number]) => void;
  zoom?: number;
}

const MapControls: React.FC<MapControlsProps> = ({ position, onPositionChange, zoom = 12 }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(position, zoom);
  }, [map, position, zoom]);
  
  // Set up map click event to move marker
  useMapEvents({
    click: (e) => {
      onPositionChange([e.latlng.lat, e.latlng.lng]);
    }
  });
  
  return null;
};

const HEARTBEAT_THRESHOLD_OPTIONS = [
  { value: 90,    label: '1h30' },
  { value: 180,   label: '3h' },
  { value: 360,   label: '6h' },
  { value: 720,   label: '12h' },
  { value: 1440,  label: '1 jour' },
  { value: 2880,  label: '2 jours' },
  { value: 5760,  label: '4 jours' },
  { value: 11520, label: '8 jours' },
  { value: 23040, label: '16 jours' },
];

const formatHeartbeatThreshold = (minutes: number): string => {
  const opt = HEARTBEAT_THRESHOLD_OPTIONS.find(o => o.value === minutes);
  if (opt) return opt.label;
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`;
};

const formatHeartbeatDuration = (lastSeen: string): string => {
  const diffMs = Date.now() - new Date(lastSeen).getTime();
  const totalMinutes = Math.floor(diffMs / 60000);
  if (totalMinutes < 1) return '< 1 min';
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const mins = totalMinutes % 60;
  if (days > 0) {
    return hours > 0 ? `${days}j ${hours}h` : `${days}j`;
  }
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
};

// Define interfaces
interface Sensor {
  id: number;
  name: string;
  serial_number: string;
  imei: string;
  ccid: string;
  location_description: string;
  axis?: string;
  direction?: string;
  pr?: string;
  is_active: boolean;
  battery_level?: number;
  signal_strength?: number;
  last_seen?: string;
  firmware_version?: string;
  created_at: string;
  updated_at: string;
  needs_firmware_upgrade: boolean;
  additional_data?: Record<string, any>;
  latitude?: number;
  longitude?: number;
  alert_threshold?: number;
  heartbeat_threshold?: number;
  photo?: string;
  suspension_status?: 'active' | 'scheduled' | null;
  location?: {
    type: string;
    coordinates: [number, number];
  };
}

interface SensorAlert {
  id: number;
  event_type: string;
  created_at: string;
  is_acknowledged: boolean;
  acknowledged_at?: string;
  acknowledger_name?: string;
  notes?: string;
  is_deleted?: boolean;
}

const SensorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sensor, setSensor] = useState<Sensor | null>(null);
  const [alerts, setAlerts] = useState<SensorAlert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [sensorName, setSensorName] = useState<string>('');
  const [sensorSerialNumber, setSensorSerialNumber] = useState<string>('');
  const [sensorImei, setSensorImei] = useState<string>('');
  const [sensorCcid, setSensorCcid] = useState<string>('');
  const [locationDescription, setLocationDescription] = useState<string>('');
  const [axis, setAxis] = useState<string>('');
  const [direction, setDirection] = useState<string>('');
  const [pr, setPr] = useState<string>('');
  const [alertThreshold, setAlertThreshold] = useState<number>(100);
  const [heartbeatThreshold, setHeartbeatThreshold] = useState<number | null>(23040);
  const [isNewSensor, setIsNewSensor] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [locationCoordinates, setLocationCoordinates] = useState<[number, number] | null>(null);
  const [deleteSensorDialogOpen, setDeleteSensorDialogOpen] = useState<boolean>(false);

  // Initialize state based on whether we're creating a new sensor
  useEffect(() => {
    if (id === 'new') {
      setIsNewSensor(true);
      setLoading(false);
      setSensorName('');
      setSensorImei('');
      setSensorCcid('');
      setLocationDescription('');
      setAxis('');
      setDirection('');
      setPr('');
      
      // Automatically try to get the user's location when creating a new sensor
      getCurrentLocation();
    } else {
      setIsNewSensor(false);
    }
  }, [id]);
  
  // Get current location and store in state
  const getCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocationCoordinates([latitude, longitude]);
          setMarkerPosition([latitude, longitude]);
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  // Handler for QR code scan success
  const handleQrCodeSuccess = (qrData: { sn: string, imei: string, ccid: string, date: string }) => {
    // Set the sensor name using the serial number
    setSensorName(`Capteur ${qrData.sn}`);
    
    // Set serial number directly from QR code
    setSensorSerialNumber(qrData.sn);
    
    // Set IMEI and CCID from QR code data
    setSensorImei(qrData.imei);
    setSensorCcid(qrData.ccid);
    
    // Set location description with manufacture date
    // setLocationDescription(t('sensor.qrCodeScanDescription', { date: qrData.date }) as string);
    
    // Automatically get current location
    getCurrentLocation();
  };

  // Format error objects into strings - wrapped in useCallback to maintain reference stability
  const formatErrorObject = useCallback((errorObj: any): string => {
    if (typeof errorObj === 'string') return errorObj;
    if (Array.isArray(errorObj)) {
      return errorObj.map(e => formatErrorObject(e)).join(', ');
    }
    if (errorObj && typeof errorObj === 'object') {
      if (errorObj.msg) return errorObj.msg;
      if (errorObj.type && errorObj.loc && errorObj.msg) {
        return `${errorObj.msg}`;
      }
      return JSON.stringify(errorObj);
    }
    return t('common.error');
  }, [t]);

  // Fetch sensor data
  const fetchSensor = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/sensors/${id}`);
      setSensor(response.data);
      setSensorName(response.data.name || '');
      setLocationDescription(response.data.location_description || '');
      setAxis(response.data.axis || '');
      setDirection(response.data.direction || '');
      setPr(response.data.pr || '');
      setAlertThreshold(response.data.alert_threshold || 100);
      setHeartbeatThreshold(response.data.heartbeat_threshold ?? 23040);

      // Fetch recent alerts for this sensor
      const alertsResponse = await apiClient.get(`/alerts/?sensor_id=${id}&limit=10`);
      setAlerts(alertsResponse.data.items);
      
    } catch (err: any) {
      console.error('Error fetching sensor data:', err);
      const errorDetail = err.response?.data?.detail;
      
      // Handle various error formats
      if (errorDetail) {
        setError(formatErrorObject(errorDetail));
      } else {
        setError(t('common.failedToLoad', { resource: t('sensors.details').toLowerCase() }));
      }
    } finally {
      setLoading(false);
    }
  }, [id, formatErrorObject, t]);

  // Load data on component mount
  useEffect(() => {
    // Only fetch if we're not creating a new sensor
    if (id && id !== 'new') {
      fetchSensor();
    }
  }, [id, fetchSensor]);

  // Subscribe to sensor update events
  useEffect(() => {
    if (id && id !== 'new') {
      // Set up event listener for sensor updates
      const unsubscribe = eventService.subscribe(EventType.SENSOR_UPDATE, (sensorData) => {
        // Update sensor data if it matches the current sensor id
        if (sensorData.id === parseInt(id)) {
          setSensor(prevSensor => {
            if (!prevSensor) return null;
            return {
              ...prevSensor,
              last_seen: sensorData.last_seen,
              battery_level: sensorData.battery_level
            };
          });
        }
      });
      
      // Clean up subscription when component unmounts
      return () => {
        unsubscribe();
      };
    }
  }, [id]);

  // Handle back button click
  const handleBack = () => {
    navigate('/sensors');
  };

  // Handle view on map
  const handleViewOnMap = () => {
    navigate('/map', { state: { highlightSensor: parseInt(id || '0') } });
  };
  
  // Location update state
  const [locationDialogOpen, setLocationDialogOpen] = useState<boolean>(false);
  const [markerPosition, setMarkerPosition] = useState<[number, number]>([48.8566, 2.3522]); // Default to Paris
  const [locationUpdateStatus, setLocationUpdateStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [locationError, setLocationError] = useState<string | null>(null);
  
  // Open location dialog and initialize marker position
  const handleLocationOpen = () => {
    if (sensor) {
      if (sensor.latitude && sensor.longitude) {
        setMarkerPosition([sensor.latitude, sensor.longitude]);
      }
      setLocationDialogOpen(true);
    }
  };
  
  // Close location dialog
  const handleLocationClose = () => {
    setLocationDialogOpen(false);
    setLocationUpdateStatus('idle');
    setLocationError(null);
  };
  
  // Handle marker position change
  const handlePositionChange = (newPosition: [number, number]) => {
    setMarkerPosition(newPosition);
  };
  
  // Handle "locate me" button
  const handleLocateMe = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMarkerPosition([latitude, longitude]);
          setLocationCoordinates([latitude, longitude]);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError(t('sensor.locationSelector.errors.locationAccessDenied'));
        }
      );
    } else {
      setLocationError(t('sensor.locationSelector.errors.geolocationNotSupported'));
    }
  };
  
  // Save sensor location
  const handleSaveLocation = async () => {
    try {
      if (!id) return;
      
      setLocationUpdateStatus('loading');
      setLocationError(null);
      
      const [latitude, longitude] = markerPosition;
      
      await apiClient.put(`/sensors/${id}/location`, {
        latitude,
        longitude,
        location_description: locationDescription,
        axis: axis,
        direction: direction,
        pr: pr
      });
      
      setLocationUpdateStatus('success');
      setTimeout(() => {
        handleLocationClose();
        fetchSensor(); // Refresh data
      }, 1500);
      
    } catch (err: any) {
      console.error('Error updating sensor location:', err);
      setLocationUpdateStatus('error');
      
      // Handle error formatting
      const errorDetail = err.response?.data?.detail;
      if (errorDetail) {
        if (typeof errorDetail === 'object') {
          setLocationError(formatErrorObject(errorDetail));
        } else {
          setLocationError(errorDetail);
        }
      } else {
        setLocationError(t('common.failedToLoad', { resource: t('sensor.location').toLowerCase() }));
      }
    }
  };

  // Handle edit dialog open
  const handleEditOpen = () => {
    if (sensor) {
      setSensorName(sensor.name);
      setLocationDescription(sensor.location_description || '');
      setAxis(sensor.axis || '');
      setDirection(sensor.direction || '');
      setPr(sensor.pr || '');
      setAlertThreshold(sensor.alert_threshold || 100);
      setHeartbeatThreshold(sensor.heartbeat_threshold ?? 23040);
      setEditDialogOpen(true);
    }
  };

  // Handle edit dialog close
  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  // Create a new sensor
  const handleCreateSensor = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});
      
      // Validate fields client-side
      let hasErrors = false;
      const errors: Record<string, string> = {};
      
      if (!sensorName.trim()) {
        errors.name = t('sensor.validationErrors.nameRequired');
        hasErrors = true;
      }
      
      if (!sensorSerialNumber.trim()) {
        errors.serial_number = t('sensor.validationErrors.serialNumberRequired');
        hasErrors = true;
      }
      
      if (!sensorImei.trim()) {
        errors.imei = t('sensor.validationErrors.imeiRequired');
        hasErrors = true;
      }
      
      if (!sensorCcid.trim()) {
        errors.ccid = t('sensor.validationErrors.ccidRequired');
        hasErrors = true;
      }
      
      if (hasErrors) {
        setValidationErrors(errors);
        setLoading(false);
        return;
      }
      
      // Prepare data for API request
      const sensorData: any = {
        name: sensorName,
        serial_number: sensorSerialNumber,
        imei: sensorImei,
        ccid: sensorCcid,
        location_description: locationDescription,
        axis: axis,
        direction: direction,
        pr: pr
      };
      
      // Add location coordinates if available
      if (locationCoordinates) {
        sensorData.latitude = locationCoordinates[0];
        sensorData.longitude = locationCoordinates[1];
      }
      
      // Send create request to the correct endpoint (with trailing slash)
      const response = await apiClient.post('/sensors/', sensorData);
      
      // Navigate to the newly created sensor
      navigate(`/sensors/${response.data.id}`);
      
    } catch (err: any) {
      console.error('Error creating sensor:', err);
      
      // Handle validation errors from the API
      if (err.response?.status === 422 && err.response?.data?.detail) {
        const apiErrors = err.response.data.detail;
        
        // API might return an array of validation errors
        if (Array.isArray(apiErrors)) {
          const formattedErrors: Record<string, string> = {};
          
          apiErrors.forEach(error => {
            if (error && typeof error === 'object') {
              // Extract field name from the location path (usually in format ['body', 'fieldname'])
              const fieldName = error.loc && Array.isArray(error.loc) && error.loc.length > 1 
                ? error.loc[error.loc.length - 1] 
                : 'form';
              
              formattedErrors[fieldName] = formatErrorObject(error);
            }
          });
          
          setValidationErrors(formattedErrors);
        } else if (typeof apiErrors === 'object') {
          // Single error object
          setError(formatErrorObject(apiErrors));
        } else {
          setError(String(apiErrors));
        }
      } else if (err.response?.status === 400) {
        // Handle 400 Bad Request errors (typically uniqueness constraints)
        const errorDetail = err.response?.data?.detail || '';
        
        if (typeof errorDetail === 'string') {
          // Check for IMEI uniqueness error
          if (errorDetail.includes('IMEI')) {
            setValidationErrors(prev => ({
              ...prev,
              imei: t('sensor.validationErrors.imeiInUse')
            }));
          }
          
          // Check for CCID uniqueness error
          if (errorDetail.includes('CCID')) {
            setValidationErrors(prev => ({
              ...prev,
              ccid: t('sensor.validationErrors.ccidInUse')
            }));
          }
          
          // If no specific field error was set, show the general error
          if (!errorDetail.includes('IMEI') && !errorDetail.includes('CCID')) {
            setError(errorDetail);
          }
        } else {
          setError(formatErrorObject(errorDetail));
        }
      } else {
        setError(t('common.failedToLoad', { resource: t('sensor.create').toLowerCase() }));
      }
      
      setLoading(false);
    }
  };

  // Handle save sensor changes
  const handleSaveSensor = async () => {
    if (isNewSensor) {
      handleCreateSensor();
      return;
    }
    
    try {
      if (!id) return;
      
      const updatePayload: Record<string, any> = {
        name: sensorName,
        location_description: locationDescription,
        axis: axis,
        direction: direction,
        pr: pr,
      };
      if (isAdmin) {
        updatePayload.alert_threshold = alertThreshold;
      }
      if (user?.role === 'ingecom') {
        updatePayload.heartbeat_threshold = heartbeatThreshold;
      }
      await apiClient.put(`/sensors/${id}`, updatePayload);
      
      setEditDialogOpen(false);
      fetchSensor(); // Refresh data
      
    } catch (err: any) {
      console.error('Error updating sensor:', err);
      
      // Handle error formatting
      const errorDetail = err.response?.data?.detail;
      if (errorDetail) {
        if (typeof errorDetail === 'object') {
          setError(formatErrorObject(errorDetail));
        } else {
          setError(errorDetail);
        }
      } else {
        setError(t('common.failedToLoad', { resource: t('sensor.edit').toLowerCase() }));
      }
    }
  };

  // Handle delete sensor
  const handleDeleteSensor = async () => {
    try {
      if (!id) return;

      // Use apiClient to delete the sensor
      await apiClient.delete(`/sensors/${id}`);

      // Close dialog
      setDeleteSensorDialogOpen(false);

      // Return to sensors list
      navigate('/sensors', { state: { snackbar: { message: t('sensor.deleteSensorSuccess') as string || 'Sensor deleted successfully', severity: 'success' } } });
    } catch (err: any) {
      console.error('Error deleting sensor:', err);
      
      // Close dialog first
      setDeleteSensorDialogOpen(false);
      
      // Handle specific error for unacknowledged alerts
      if (err.response?.status === 400 && err.response?.data?.detail?.type === 'sensor_has_unacknowledged_alerts') {
        const unacknowledgedCount = err.response.data.detail.unacknowledged_count;
        setError(t('sensor.deleteSensorWithUnacknowledgedAlerts', { count: unacknowledgedCount }));
      } else {
        // Handle other errors
        const errorDetail = err.response?.data?.detail;
        if (typeof errorDetail === 'string') {
          setError(errorDetail);
        } else if (errorDetail?.message) {
          setError(errorDetail.message);
        } else {
          setError(t('sensor.deleteSensorError') || t('common.failedToLoad', { resource: t('common.delete').toLowerCase() }));
        }
      }
    }
  };

  // Handle open delete sensor dialog
  const handleOpenDeleteSensorDialog = () => {
    setDeleteSensorDialogOpen(true);
  };

  // Handle close delete sensor dialog
  const handleCloseDeleteSensorDialog = () => {
    setDeleteSensorDialogOpen(false);
  };

  // Refresh data
  const handleRefresh = () => {
    fetchSensor();
  };

  // View alert details
  const handleViewAlert = (alertId: number) => {
    navigate(`/alerts/${alertId}`);
  };

  // Handle test device
  const handleTestDevice = () => {
    navigate(`/test/${id}`);
  };

  // State for delete alerts dialog and notifications
  const [deleteSelectedAlertDialogOpen, setDeleteSelectedAlertDialogOpen] = useState<boolean>(false);
  const [selectedAlerts, setSelectedAlerts] = useState<number[]>([]);
  const [deleteAlertStatus, setDeleteAlertStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [actionMode, setActionMode] = useState<'acknowledge' | 'delete'>('acknowledge');
  const [acknowledgeDialogOpen, setAcknowledgeSelectedAlertsDialogOpen] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  
  // Photo management state
  const [photoUploading, setPhotoUploading] = useState<boolean>(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Suspension state
  const [suspensions, setSuspensions] = useState<SuspensionRead[]>([]);
  const [suspensionFormOpen, setSuspensionFormOpen] = useState(false);
  const [suspensionDeleteTarget, setSuspensionDeleteTarget] = useState<SuspensionRead | null>(null);
  const [suspensionDeleting, setSuspensionDeleting] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'ingecom';

  const fetchSuspensions = useCallback(async () => {
    if (!id || id === 'new') return;
    try {
      const res = await suspensionApi.list({ sensor_id: Number(id), include_past: false, page: 1, page_size: 10 });
      // Garder actives + programmées uniquement
      setSuspensions(res.data.items.filter(s => s.state === 'active' || s.state === 'scheduled'));
    } catch {
      // silencieux
    }
  }, [id]);

  useEffect(() => { fetchSuspensions(); }, [fetchSuspensions]);

  const handleSuspensionDeleteConfirm = async () => {
    if (!suspensionDeleteTarget) return;
    setSuspensionDeleting(true);
    try {
      await suspensionApi.delete(suspensionDeleteTarget.id);
      setSuspensionDeleteTarget(null);
      fetchSuspensions();
      // Rafraîchir le capteur pour mettre à jour le chip suspension_status en haut
      if (id) {
        const res = await apiClient.get(`/sensors/${id}`);
        setSensor(res.data);
      }
    } catch (e: any) {
      // silencieux
    } finally {
      setSuspensionDeleting(false);
    }
  };

  // Handle delete selected acknowledged alerts
  const handleDeleteSelectedAcknowledgedAlerts = async () => {
    if (selectedAlerts.length === 0) return;

    try {
      setDeleteAlertStatus('loading');

      const acknowledgedAlertIds = alerts
      .filter(alert => selectedAlerts.includes(alert.id) && alert.is_acknowledged)
      .map(alert => alert.id);

      if (acknowledgedAlertIds.length === 0) {
        throw new Error(t('sensor.noAcknowledgedAlertsSelected') || 'No acknowledged alerts selected for deletion');
      }
    
      // Use direct fetch with correct API URL to handle 204 No Content response
      const responseList = await Promise.all(
        acknowledgedAlertIds.map(id =>
          fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'}/alerts/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          })
        )
      );

      const allSucceeded = responseList.every(response => response.ok);

      // Check if response is ok without attempting to parse the body
      if (allSucceeded) {
        setDeleteAlertStatus('success');
        setAlertMessage(t('sensor.deleteAcknowledgedAlertsSuccess') || 'Acknowledged selected alerts deleted successfully');
                
        // Close dialog
        setDeleteSelectedAlertDialogOpen(false);

        // Refresh selected alerts
        setSelectedAlerts([]);

        // Refresh alerts
        fetchSensor();
      } else {
        throw new Error(t('sensor.deleteAcknowledgedAlertsError') || 'Failed to delete selected alerts');
      }
    } catch (err: any) {
      console.error('Error deleting selected alerts:', err);
      setDeleteAlertStatus('error');
      setAlertMessage(t('sensor.deleteAcknowledgedAlertsError') || 'Failed to delete selected alerts');
    }
  };

  // Handle open delete selected acknowledged alerts dialog
  const handleOpenDeleteSelectedAcknowledgedAlertsDialog = () => {
    setDeleteSelectedAlertDialogOpen(true);
  };

  // Handle close delete selected acknowledged alerts dialog
  const handleCloseDeleteSelectedAcknowledgedAlertsDialog = () => {
    setDeleteSelectedAlertDialogOpen(false);
  };

  // Handle click to acknowledge selected alerts
    const handleAcknowledgeSelectedAlerts = async () => {
      try {
        setLoading(true);
        const acknowledgePromises = alerts
          .filter(alert => 
            selectedAlerts.includes(alert.id) && !alert.is_acknowledged
          )
          .map(alert => 
            apiClient.post('/alerts/acknowledge', {
              alert_id: alert.id, 
              notes: JSON.stringify({
                system_note_key: 'alerts.acknowledge_note',
                user_note: notes
              })
            })
          );
        
        await Promise.all(acknowledgePromises);
  
        // Empty the selected alerts after acknowledging
        setSelectedAlerts([]);
        setAcknowledgeSelectedAlertsDialogOpen(false);
        setNotes('');
        fetchSensor();
      } catch (err: any) {
        console.error('Error acknowledging alerts:', err);
        setError(
          err.response?.data?.detail || 
          t('common.failedToLoad', { resource: t('alerts.acknowledge').toLowerCase() })
        );
      } finally {
        setLoading(false);
      }
    };
  
    // Handle open delete selected acknowledged alerts dialog
    const handleAcknowledgeSelectedAlertsOpen = () => {
      setAcknowledgeSelectedAlertsDialogOpen(true);
    };
  
    // Handle close delete selected acknowledged alerts dialog
    const handleAcknowledgeSelectedAlertsClose = () => {
      setAcknowledgeSelectedAlertsDialogOpen(false);
    };

  const getSelectableIds = (list: SensorAlert[], mode: string) => {
    switch (mode) {
      case "acknowledge":
        return list.filter(a => !a.is_acknowledged).map(a => a.id);
      case "delete":
        return list.filter(a => a.is_acknowledged).map(a => a.id);
      default:
        return [];
    }
  };

  // Get count of unacknowledged alerts
    const unacknowledgedCount = alerts.filter(alert => !alert.is_acknowledged).length;

    useEffect(() => {
      const selectableIds = getSelectableIds(alerts, actionMode);
      setSelectedAlerts(prev =>
        prev.filter(id => selectableIds.includes(id))
      );
    }, [actionMode, alerts]);

  // Handle photo update
  const handlePhotoUpdate = async (photoData: string) => {
    if (!id || id === 'new') return;
    
    try {
      setPhotoUploading(true);
      setPhotoError(null);
      
      console.log('Converting photo data to blob, size:', photoData.length);
      
      // Convert data URL to blob for upload with better error handling
      let blob: Blob;
      try {
        // Method 1: Use fetch (works well on most browsers)
        const response = await fetch(photoData);
        if (!response.ok) {
          throw new Error(`Fetch failed: ${response.status}`);
        }
        blob = await response.blob();
        
        // Validate blob
        if (!blob || blob.size === 0) {
          throw new Error('Empty blob created');
        }
        
        console.log('Blob created successfully:', blob.type, blob.size, 'bytes');
        
      } catch (fetchError) {
        console.warn('Fetch method failed, trying manual conversion:', fetchError);
        
        // Method 2: Manual conversion (fallback for mobile)
        try {
          // Extract base64 data
          const base64Data = photoData.split(',')[1];
          if (!base64Data) {
            throw new Error('Invalid data URL format');
          }
          
          // Convert base64 to binary
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Create blob
          const mimeType = photoData.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
          blob = new Blob([bytes], { type: mimeType });
          
          console.log('Manual blob conversion successful:', blob.type, blob.size, 'bytes');
          
        } catch (manualError) {
          console.error('Manual conversion also failed:', manualError);
          throw new Error('Failed to convert image data');
        }
      }
      
      // Validate final blob
      if (blob.size < 100) {
        throw new Error('Image data too small, possibly corrupted');
      }
      
      if (blob.size > 10 * 1024 * 1024) { // 10MB limit
        throw new Error('Image file too large');
      }
      
      // Create form data
      const formData = new FormData();
      formData.append('photo', blob, 'sensor-photo.jpg');
      
      console.log('Uploading photo, FormData created');
      
      // Upload photo
      const uploadResponse = await apiClient.post(`/sensors/${id}/photo`, formData);
      
      console.log('Photo uploaded successfully');
      
      // Update sensor data
      setSensor(uploadResponse.data);
      
    } catch (err: any) {
      console.error('Error uploading photo:', err);
      setPhotoError(
        err.response?.data?.detail ||
        err.message ||
        t('sensor.photo.uploadError') ||
        'Error uploading photo'
      );
    } finally {
      setPhotoUploading(false);
    }
  };

  // Handle photo delete
  const handlePhotoDelete = async () => {
    if (!id || id === 'new') return;
    
    try {
      setPhotoUploading(true);
      setPhotoError(null);
      
      // Delete photo
      const response = await apiClient.delete(`/sensors/${id}/photo`);
      
      // Update sensor data
      setSensor(response.data);
      
    } catch (err: any) {
      console.error('Error deleting photo:', err);
      setPhotoError(
        err.response?.data?.detail ||
        t('sensor.photo.deleteError') ||
        'Error deleting photo'
      );
    } finally {
      setPhotoUploading(false);
    }
  };

  // Render content based on mode (new or existing)
  if (loading && !sensor && !isNewSensor) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // New sensor form
  if (isNewSensor) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">{t('sensor.create')}</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
          {/* QR Code Scanner avec formulaire intercalé */}
          <QrScannerComponent onScanSuccess={handleQrCodeSuccess}>
            {/* Formulaire des champs du capteur intercalé */}
            <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                label={t('sensor.fields.name')}
                required
                fullWidth
                value={sensorName}
                onChange={(e) => setSensorName(e.target.value)}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('sensor.fields.serialNumber')}
                required
                fullWidth
                value={sensorSerialNumber}
                onChange={(e) => setSensorSerialNumber(e.target.value)}
                error={!!validationErrors.serial_number}
                helperText={validationErrors.serial_number}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('sensor.fields.imei')}
                required
                fullWidth
                value={sensorImei}
                onChange={(e) => setSensorImei(e.target.value)}
                error={!!validationErrors.imei}
                helperText={validationErrors.imei}
                placeholder={t('sensor.placeholders.imei') as string}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('sensor.fields.ccid')}
                required
                fullWidth
                value={sensorCcid}
                onChange={(e) => setSensorCcid(e.target.value)}
                error={!!validationErrors.ccid}
                helperText={validationErrors.ccid}
                placeholder={t('sensor.placeholders.ccid') as string}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('sensor.fields.locationDescription')}
                fullWidth
                multiline
                rows={3}
                value={locationDescription}
                onChange={(e) => setLocationDescription(e.target.value)}
                error={!!validationErrors.location_description}
                helperText={validationErrors.location_description}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('sensor.fields.axis')}
                fullWidth
                value={axis}
                onChange={(e) => setAxis(e.target.value)}
                error={!!validationErrors.axis}
                helperText={validationErrors.axis}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('sensor.fields.direction')}
                fullWidth
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                error={!!validationErrors.direction}
                helperText={validationErrors.direction}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('sensor.fields.pr')}
                fullWidth
                value={pr}
                onChange={(e) => setPr(e.target.value)}
                error={!!validationErrors.pr}
                helperText={validationErrors.pr}
              />
            </Grid>
            </Grid>
          </QrScannerComponent>

          {/* Boutons d'action en bas */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSensor}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('sensors.add')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Existing sensor details
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{t('sensor.details')}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={t("sensorDetails.RefreshSensor")}>
          <IconButton onClick={handleRefresh} sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        {user?.role === 'ingecom' && ( // Only show for Ingecom users
          <Tooltip title={t('testDevice.title')}>
            <IconButton onClick={handleTestDevice} sx={{ mr: 1 }} color="primary">
              <BugReportIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t("sensorDetails.EditSensor")}>
          <IconButton onClick={handleEditOpen} sx={{ mr: 1 }}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        {user?.is_admin && ( // Verify if user is admin
        <Tooltip title={t("sensorDetails.DeleteSensor")}>
          <IconButton onClick={handleOpenDeleteSensorDialog} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        )}
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              {t('common.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {sensor && (
        <Grid container spacing={3}>
          {/* Sensor Overview */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  {sensor.name}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip 
                  label={sensor.is_active ? t('sensor.status_active') : t('sensor.status_inactive')}
                  color={sensor.is_active ? 'success' : 'error'} 
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('sensor.identifiers')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">{t('sensor.fields.serialNumber')}:</Typography>
                    <Typography variant="body1" fontWeight="bold">{sensor.serial_number}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">IMEI:</Typography>
                    <Typography variant="body1" fontWeight="bold">{sensor.imei}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">CCID:</Typography>
                    <Typography variant="body1" fontWeight="bold">{sensor.ccid}</Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('sensor.location')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">
                      {sensor.location_description || t('sensor.locationNotSet')}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    {(sensor.latitude && sensor.longitude) && (
                      <Button
                        variant="outlined"
                        startIcon={<TravelExploreIcon />}
                        onClick={handleViewOnMap}
                        size="small"
                      >
                        {t('sensor.viewOnMap')}
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<LocationOnIcon />}
                      onClick={handleLocationOpen}
                      size="small"
                      color="primary"
                    >
                      {(sensor.latitude && sensor.longitude) ? t('sensor.updateLocation') : t('sensor.setLocation')}
                    </Button>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('sensor.status')}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {sensor.battery_level && sensor.battery_level > 3000 ? (
                              <BatteryFullIcon color="success" sx={{ mr: 1 }} />
                            ) : (
                              <BatteryAlertIcon color="error" sx={{ mr: 1 }} />
                            )}
                            <Typography variant="body2">
                              {t('sensor.battery')}: {sensor.battery_level ? `${(sensor.battery_level / 1000).toFixed(2)}V` : 'Unknown'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('sensor.firmware')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">{t('sensor.version')}:</Typography>
                    <Typography variant="body1">{sensor.firmware_version || 'Unknown'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">{t('sensor.needsUpgrade')}:</Typography>
                    <Chip 
                      label={sensor.needs_firmware_upgrade ? t('sensor.yes') : t('sensor.no')}
                      color={sensor.needs_firmware_upgrade ? 'warning' : 'success'} 
                      size="small"
                    />
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('sensor.dates')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">{t('sensor.lastSeen')}:</Typography>
                    <Typography variant="body1">
                      {sensor.last_seen ? formatLocalDateTime(sensor.last_seen) : t('sensor.never')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">{t('sensor.created')}:</Typography>
                    <Typography variant="body1">
                      {formatLocalDateTime(sensor.created_at)}
                    </Typography>
                  </Box>
                </Box>

                {user?.role === 'ingecom' && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('sensor.heartbeat.title')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body2">{t('sensor.heartbeat.duration')}:</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {sensor.last_seen ? formatHeartbeatDuration(sensor.last_seen) : t('sensor.never')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body2">{t('sensor.heartbeat.threshold')}:</Typography>
                      <Typography variant="body1">
                        {formatHeartbeatThreshold(sensor.heartbeat_threshold ?? 23040)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>
      
          {/* Recent Alerts */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('sensor.recentAlerts')}
                </Typography>
                {unacknowledgedCount > 0 && selectedAlerts.length > 0 && !alerts.find(a => selectedAlerts.includes(a.id) && a.is_acknowledged) && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleAcknowledgeSelectedAlertsOpen}
                  >
                    {t('alerts.acknowledge')} ({selectedAlerts.length})
                  </Button>
                )}
              </Box>          
              
              <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="action-label">{t('alerts.actionMode')}</InputLabel>
                    <Select
                      labelId="action-label"
                      value={actionMode}
                      label={t('alerts.actionMode')}
                      onChange={e => setActionMode(e.target.value as 'acknowledge' | 'delete')}
                    >
                      <MenuItem value="acknowledge">
                        <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                        {t('alerts.acknowledge')}
                      </MenuItem>
                      {alerts.some(alert => alert.is_acknowledged) && user?.is_admin && ( // Verify if user is admin
                        <MenuItem value="delete">
                          <DeleteIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                          {t('common.delete')}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              
              {alerts.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('sensor.noAlertsForSensor')}
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Tooltip title={t('sensor.selectAllAcknowledged')} arrow>
                            <Checkbox
                              indeterminate={
                                (() => {
                                  const selectableIds = getSelectableIds(alerts, actionMode);
                                  const selectedCount = selectedAlerts.filter(id => selectableIds.includes(id)).length;
                                  return selectedCount > 0 && selectedCount < selectableIds.length;
                                })()
                              }
                              checked={
                                (() => {
                                  const selectableIds = getSelectableIds(alerts, actionMode);
                                  return (
                                    selectableIds.length > 0 &&
                                    selectableIds.every(id => selectedAlerts.includes(id))
                                  );
                                })()
                              }
                              disabled={getSelectableIds(alerts, actionMode).length === 0}
                              onChange={() => {
                                const selectableIds = getSelectableIds(alerts, actionMode);
                                const allSelected = selectedAlerts.filter(id => selectableIds.includes(id)).length;
                                if (allSelected > 0) {
                                  setSelectedAlerts(selectedAlerts.filter(id => !selectableIds.includes(id)));
                                } else {
                                  setSelectedAlerts([
                                    ...selectedAlerts.filter(id => !selectableIds.includes(id)),
                                    ...selectableIds,
                                  ]);
                                }
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{t('common.eventType')}</TableCell>
                        <TableCell>{t('common.date')}</TableCell>
                        <TableCell>{t('sensors.status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts.map((alert) => {
                        const isSelected = selectedAlerts.includes(alert.id);

                        return(
                        <TableRow 
                          key={alert.id}
                          hover
                          selected={isSelected}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                          onClick={() => handleViewAlert(alert.id)}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              disabled={
                                (actionMode === "acknowledge" && alert.is_acknowledged) ||
                                (actionMode === "delete" && !alert.is_acknowledged)
                              }
                              onChange={() => {
                                setSelectedAlerts(prev =>
                                  isSelected
                                    ? prev.filter(id => id !== alert.id)
                                    : [...prev, alert.id]
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>{t(`eventTypes.${alert.event_type}`, t('eventTypes.default'))}</TableCell>
                          <TableCell>{formatLocalDateTime(alert.created_at)}</TableCell>
                          <TableCell>
                            {alert.is_deleted ? (
                              <Chip 
                                icon={<DeleteOutlineIcon />}
                                label={t('common.deleted')}
                                color="error" 
                                size="small" 
                              />
                            ) : alert.is_acknowledged ? (
                              <Chip 
                                icon={<CheckCircleOutlineIcon />}
                                label={t('dashboard.acknowledged')}
                                color="success" 
                                size="small" 
                              />
                            ) : (
                              <Chip 
                                icon={<ErrorOutlineIcon />}
                                label={t('dashboard.unacknowledged')}
                                color="warning" 
                                size="small" 
                              />
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                
                {alerts.some(alert => alert.is_acknowledged) && user?.is_admin && ( // Verify if user is admin
                  <Button 
                    variant="outlined" 
                    size="small" 
                    color="error"
                    onClick={handleOpenDeleteSelectedAcknowledgedAlertsDialog}
                    disabled={selectedAlerts.filter(id => alerts.find(a => a.id === id && a.is_acknowledged)).length === 0}
                  >
                    {t('sensor.deleteAcknowledgedAlerts')}
                  </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => navigate(`/alerts?sensor_id=${id}`)}
                >
                  {t('sensor.viewAllAlerts')}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Sensor Photo */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {t('sensor.photo.title')}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              {photoError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {photoError}
                </Alert>
              )}
              
              <SensorPhotoCapture
                currentPhoto={sensor.photo}
                onPhotoUpdate={handlePhotoUpdate}
                onPhotoDelete={handlePhotoDelete}
                loading={photoUploading}
                disabled={loading}
              />
            </Paper>
          </Grid>

          {/* Suspension d'alerte */}
          {!isNewSensor && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PauseCircleOutlineIcon color={sensor?.suspension_status === 'active' ? 'error' : 'action'} />
              <Typography variant="h6">{t('suspensions.title')}</Typography>
              {sensor?.suspension_status === 'active' && (
                <Chip label={t('suspensions.states.active')} color="error" size="small" />
              )}
              {sensor?.suspension_status === 'scheduled' && (
                <Chip label={t('suspensions.states.scheduled')} color="primary" size="small" />
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              {isAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setSuspensionFormOpen(true)}
                >
                  {t('suspensions.newButton')}
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                onClick={() => navigate(`/suspensions?sensor_id=${id}`)}
              >
                {t('suspensions.viewAll')}
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {suspensions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {t('suspensions.noneForSensor')}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {suspensions.map((susp) => (
                <Box
                  key={susp.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: susp.state === 'active' ? 'error.light' : 'divider',
                    borderRadius: 1,
                    bgcolor: susp.state === 'active' ? 'error.50' : 'background.default',
                  }}
                >
                  <Stack spacing={0.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={t(`suspensions.states.${susp.state}`)}
                        color={susp.state === 'active' ? 'error' : 'primary'}
                        size="small"
                      />
                      <Typography variant="body2">
                        {new Date(susp.start_at).toLocaleString()} → {susp.is_indefinite ? t('suspensions.indefinite') : new Date(susp.end_at).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t('suspensions.columns.createdBy')} : {susp.creator_name ?? `#${susp.created_by}`}
                    </Typography>
                  </Stack>
                  {isAdmin && (
                    <Tooltip title={t('suspensions.delete.openDialog')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setSuspensionDeleteTarget(susp)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Stack>
          )}
            </Paper>
          </Grid>
          )}
        </Grid>
      )}

      {/* Dialog confirmation suppression suspension */}
      <SuspensionDeleteDialog
        open={!!suspensionDeleteTarget}
        onClose={() => setSuspensionDeleteTarget(null)}
        onConfirm={handleSuspensionDeleteConfirm}
        loading={suspensionDeleting}
        suspension={suspensionDeleteTarget}
        sensorName={sensor?.name ?? sensor?.serial_number}
      />

      {/* SuspensionFormDialog */}
      {suspensionFormOpen && (
        <SuspensionFormDialog
          open={suspensionFormOpen}
          onClose={() => setSuspensionFormOpen(false)}
          onSuccess={async () => {
            setSuspensionFormOpen(false);
            fetchSuspensions();
            if (id) {
              const res = await apiClient.get(`/sensors/${id}`);
              setSensor(res.data);
            }
          }}
          preselectedSensorId={Number(id)}
        />
      )}

      {/* Edit Sensor Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('sensor.edit')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('sensor.fields.name')}
            fullWidth
            variant="outlined"
            value={sensorName}
            onChange={(e) => setSensorName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('sensor.fields.locationDescription')}
            fullWidth
            variant="outlined"
            value={locationDescription}
            onChange={(e) => setLocationDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('sensor.fields.axis')}
            fullWidth
            variant="outlined"
            value={axis}
            onChange={(e) => setAxis(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('sensor.fields.direction')}
            fullWidth
            variant="outlined"
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('sensor.fields.pr')}
            fullWidth
            variant="outlined"
            value={pr}
            onChange={(e) => setPr(e.target.value)}
            sx={{ mb: 2 }}
          />
          {isAdmin && (
            <TextField
              margin="dense"
              label={t('sensor.fields.alertThreshold')}
              fullWidth
              variant="outlined"
              type="number"
              value={alertThreshold}
              onChange={(e) => setAlertThreshold(parseInt(e.target.value) || 100)}
              inputProps={{ min: 1, max: 1000 }}
              helperText={t('sensor.fields.alertThresholdHelp')}
              sx={{ mb: 2 }}
            />
          )}
          {user?.role === 'ingecom' && (
            <FormControl fullWidth margin="dense">
              <InputLabel id="heartbeat-threshold-label">{t('sensor.heartbeat.threshold')}</InputLabel>
              <Select
                labelId="heartbeat-threshold-label"
                value={heartbeatThreshold ?? ''}
                label={t('sensor.heartbeat.threshold')}
                onChange={(e) => setHeartbeatThreshold(e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value="">{t('common.notSet')}</MenuItem>
                {HEARTBEAT_THRESHOLD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveSensor} variant="contained">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Acknowledge Dialog */}
      <Dialog open={acknowledgeDialogOpen} onClose={handleAcknowledgeSelectedAlertsClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('alertDetail.dialog.titleSelected')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('alertDetail.dialog.acknowledgingSelected', {
              list: selectedAlerts
                .map(id => {
                  const alert = alerts.find(a => a.id === id);
                  return alert ? `#${id} (${t(`eventTypes.${alert.event_type}`)})` : `#${id}`;
                })
                .join(', ')
            })}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('alertDetail.dialog.explanationSelected')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('alertDetail.dialog.notesLabel')}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('alertDetail.dialog.notesPlaceholderSelected') as string}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAcknowledgeSelectedAlertsClose}>{t('alertDetail.dialog.cancel')}</Button>
          <Button onClick={handleAcknowledgeSelectedAlerts} variant="contained" color="primary">
            {t('alertDetail.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Sensor Dialog */}
      <Dialog open={deleteSensorDialogOpen} onClose={handleCloseDeleteSensorDialog}>
        <DialogTitle>{t('sensor.deleteSensor')}</DialogTitle>
        <DialogContent>
          <Typography>
        {t('sensor.deleteSensorConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteSensorDialog}>
        {t('common.cancel')}
          </Button>
          <Button 
        onClick={handleDeleteSensor} 
        color="error" 
        variant="contained"
        disabled={deleteAlertStatus === 'loading'}
          >
        {deleteAlertStatus === 'loading' ? <CircularProgress size={24} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Selected Acknowledged Alerts Dialog */}
      {user?.is_admin && ( // Verify if user is admin
        <Dialog open={deleteSelectedAlertDialogOpen} onClose={handleCloseDeleteSelectedAcknowledgedAlertsDialog}>
          <DialogTitle>{t('sensor.deleteAcknowledgedAlerts')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('sensor.deleteAcknowledgedAlertsConfirm')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteSelectedAcknowledgedAlertsDialog}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteSelectedAcknowledgedAlerts} 
              color="error" 
              variant="contained"
              disabled={deleteAlertStatus === 'loading'}
            >
              {deleteAlertStatus === 'loading' ? <CircularProgress size={24} /> : t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={deleteAlertStatus === 'success' || deleteAlertStatus === 'error'}
        autoHideDuration={6000}
        onClose={() => setDeleteAlertStatus('idle')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setDeleteAlertStatus('idle')} 
          severity={deleteAlertStatus === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Location Selection Dialog */}
      <Dialog open={locationDialogOpen} onClose={handleLocationClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('sensor.setLocation')}</DialogTitle>
        <DialogContent>
          {locationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {locationError}
            </Alert>
          )}
          
          {locationUpdateStatus === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('sensor.locationUpdated')}
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('sensor.locationSelector.instructions')}
          </Typography>
          
          <Box sx={{ height: '400px', width: '100%', mt: 2, mb: 2, position: 'relative' }}>
            <MapContainer
              center={markerPosition}
              zoom={12}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              <Marker 
                position={markerPosition}
                draggable={true}
                eventHandlers={{
                  dragend: (e) => {
                    const marker = e.target;
                    const position = marker.getLatLng();
                    setMarkerPosition([position.lat, position.lng]);
                  }
                }}
              >
                <Popup>
                  <Typography variant="body2">
                    {t('sensor.locationSelector.latitude')}: {markerPosition[0].toFixed(6)}<br />
                    {t('sensor.locationSelector.longitude')}: {markerPosition[1].toFixed(6)}
                  </Typography>
                </Popup>
              </Marker>
              
              <MapControls
                position={markerPosition}
                onPositionChange={handlePositionChange}
              />
            </MapContainer>
            
            <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}>
              <Tooltip title={t('sensor.locationSelector.useMyLocation')}>
                <IconButton 
                  onClick={handleLocateMe} 
                  sx={{ backgroundColor: 'white', '&:hover': { backgroundColor: '#f5f5f5' } }}
                >
                  <MyLocationIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={t('sensor.locationSelector.latitude')}
                fullWidth
                value={markerPosition[0].toFixed(6)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('sensor.locationSelector.longitude')}
                fullWidth
                value={markerPosition[1].toFixed(6)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('sensor.locationSelector.description')}
                fullWidth
                value={locationDescription}
                onChange={(e) => setLocationDescription(e.target.value)}
                helperText={t('sensor.locationSelector.descriptionHelp')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('sensor.fields.axis')}
                fullWidth
                value={axis}
                onChange={(e) => setAxis(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('sensor.fields.direction')}
                fullWidth
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('sensor.fields.pr')}
                fullWidth
                value={pr}
                onChange={(e) => setPr(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLocationClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSaveLocation} 
            variant="contained" 
            disabled={locationUpdateStatus === 'loading'}
            startIcon={locationUpdateStatus === 'loading' ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SensorDetail;